/**
 * "Create a company" for an enquiry — find it, or make it, then attach it.
 *
 * Owner's decision, 2026-10-01: an enquiry row offers Create a company. Step one
 * lists the companies whose name is similar to the institution the enquiry
 * names; step two either attaches one of them or opens the ordinary New company
 * form PREFILLED with the institution name and country, and attaches the result.
 * A company made here goes through the normal approval round — `createCompany`
 * raises it, exactly as it does from `/companies`.
 *
 * ⛔ **NOTHING IS AUTO-SELECTED.** School-level names share long prefixes by
 * design (`Boston University - Questrom School of Business` and `Trustees of
 * Boston University` bill separately), so a wrong attach is a confident wrong
 * answer every screen downstream renders perfectly. Each match is its own
 * explicit action, and "None of these" is always offered — whether the lookup
 * found five, none, or failed.
 *
 * 🔴 **NO ROLLBACK IS CLAIMED.** Create and attach are two writes with nothing
 * spanning them. If the company was created and the attach then failed, the
 * company EXISTS — awaiting approval, approver emailed — and the only truthful
 * report says so and offers to attach it again. Never "nothing was saved".
 *
 * ⚠️ The caller owns the attach (`link`), because the three enquiry sources
 * write it through three different routes. Refusals are routed on
 * `error.code`, never on the message, and the server's sentence is shown beside
 * ours.
 *
 * ⚠️ It does not close itself after a successful attach: `onLinked` is the end
 * of the flow, and the caller closes it there.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { CompanyFormModal } from '@/components/companies/CompanyFormModal';
import { Button, Modal, Skeleton } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import type { Company, SimilarCompany } from '@/lib/types';

export interface EnquiryCompanySeed {
  name: string;
  countryId: number | null;
}

export interface CreateCompanyForEnquiryProps {
  open: boolean;
  seed: EnquiryCompanySeed | null;
  onClose: () => void;
  /** Persist the link; throw ApiError on refusal. */
  link: (companyId: number) => Promise<void>;
  /** Called after link() resolved. */
  onLinked: (company: { id: number; name: string }) => void;
  /**
   * The label of the New company form's post-create confirmation button
   * (`CompanyFormModal`'s `createdContinueLabel`). Default "Continue"; the
   * contact flow names its next step instead.
   */
  createdContinueLabel?: string;
}

/** How many similar companies to ask for. Matches `CompanyFormModal`'s own. */
const SIMILAR_LIMIT = 5;

type Lookup =
  | { status: 'loading' }
  | { status: 'ready'; matches: SimilarCompany[] }
  | { status: 'failed' }
  | { status: 'noName' };

type Stage =
  | { name: 'choosing' }
  | { name: 'creating' }
  /** The company was CREATED here and the attach was refused or failed. */
  | { name: 'createdNotLinked'; company: { id: number; name: string }; message: string };

/**
 * Words a refused attach. Routed on the CODE — the three enquiry sources each
 * spell their own, so the match is on the suffix both share rather than on a
 * message. An unrecognised refusal falls through to the server's own sentence.
 */
function linkFailureSentence(error: unknown): string {
  if (error instanceof ApiError) {
    const server = error.formMessage ?? error.fieldErrors.companyId ?? error.message;
    if (error.code.endsWith('_COMPANY_ALREADY_LINKED')) {
      return `This enquiry already has a company attached. ${server}`;
    }
    if (error.code.endsWith('_COMPANY_UNKNOWN')) {
      return `That company could not be found. ${server}`;
    }
    if (error.status === 403) {
      return `Your account is not allowed to attach a company to this enquiry. ${server}`;
    }
    return server;
  }
  return 'The company could not be attached. Check your connection and try again.';
}

export function CreateCompanyForEnquiry({
  open,
  seed,
  onClose,
  link,
  onLinked,
  createdContinueLabel = 'Continue',
}: CreateCompanyForEnquiryProps) {
  if (!open || seed === null) return null;
  return (
    <EnquiryCompanyFlow
      // Remounted per seed, so one enquiry's lookup never survives into the next.
      key={`${seed.name}:${seed.countryId ?? ''}`}
      seed={seed}
      onClose={onClose}
      link={link}
      onLinked={onLinked}
      createdContinueLabel={createdContinueLabel}
    />
  );
}

function EnquiryCompanyFlow({
  seed,
  onClose,
  link,
  onLinked,
  createdContinueLabel,
}: {
  seed: EnquiryCompanySeed;
  onClose: () => void;
  link: (companyId: number) => Promise<void>;
  onLinked: (company: { id: number; name: string }) => void;
  createdContinueLabel: string;
}) {
  const typedName = seed.name.trim();
  const [lookup, setLookup] = useState<Lookup>(
    typedName === '' ? { status: 'noName' } : { status: 'loading' },
  );
  const [stage, setStage] = useState<Stage>({ name: 'choosing' });
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A ref as well as state: two clicks in one tick both read state as idle.
  const busy = useRef(false);

  useEffect(() => {
    if (typedName === '') return;
    const controller = new AbortController();
    api
      .listSimilarCompanies({ name: typedName, limit: SIMILAR_LIMIT }, controller.signal)
      .then((answer) => {
        if (!controller.signal.aborted) setLookup({ status: 'ready', matches: answer.data });
      })
      .catch(() => {
        // A failed lookup is NOT "nothing similar" — it is said as a failure.
        if (!controller.signal.aborted) setLookup({ status: 'failed' });
      });
    return () => controller.abort();
  }, [typedName]);

  const attach = useCallback(
    async (company: { id: number; name: string }, created: boolean) => {
      if (busy.current) return;
      busy.current = true;
      setLinkingId(company.id);
      setError(null);
      try {
        await link(company.id);
        onLinked(company);
      } catch (caught) {
        const message = linkFailureSentence(caught);
        if (created) setStage({ name: 'createdNotLinked', company, message });
        else setError(message);
      } finally {
        busy.current = false;
        setLinkingId(null);
      }
    },
    [link, onLinked],
  );

  const title = `Create a company for ${typedName === '' ? 'this enquiry' : typedName}`;

  if (stage.name === 'creating') {
    return (
      <CompanyFormModal
        open
        prefill={{ name: seed.name, countryId: seed.countryId }}
        createdContinueLabel={createdContinueLabel}
        /*
         * Closing the form before a save is a step back: nothing was created.
         * After a save, `onSaved` fires only from the form's approval
         * confirmation (its button or its close), so the attach runs after it.
         */
        onClose={() => setStage({ name: 'choosing' })}
        onSaved={(company: Company) => {
          setStage({ name: 'choosing' });
          void attach({ id: company.id, name: company.name }, true);
        }}
      />
    );
  }

  if (stage.name === 'createdNotLinked') {
    const { company, message } = stage;
    return (
      <Modal
        open
        onClose={onClose}
        title={title}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              loading={linkingId === company.id}
              onClick={() => void attach(company, true)}
            >
              Attach it again
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm">
          <p className="text-fg">
            <span className="font-medium">{company.name}</span> was created and is awaiting
            approval, but it was not attached to this enquiry.
          </p>
          <p role="alert" className="text-danger">
            {message}
          </p>
        </div>
      </Modal>
    );
  }

  const linking = linkingId !== null;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <Button variant="secondary" onClick={onClose} disabled={linking}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-3">
        {error !== null && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}

        {lookup.status === 'loading' && (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        {lookup.status === 'failed' && (
          <p role="status" className="text-xs text-fg-muted">
            Couldn’t check for companies with a similar name.
          </p>
        )}
        {lookup.status === 'noName' && (
          <p role="status" className="text-xs text-fg-muted">
            The enquiry records no institution name, so nothing could be looked up.
          </p>
        )}
        {lookup.status === 'ready' && lookup.matches.length === 0 && (
          <p role="status" className="text-xs text-fg-muted">
            No company on file looks similar to this name.
          </p>
        )}

        {lookup.status === 'ready' && lookup.matches.length > 0 && (
          <ul className="space-y-2">
            {lookup.matches.map((match) => {
              const meta = [match.shortName, match.stage?.name ?? null, match.city].filter(
                (part): part is string => typeof part === 'string' && part.trim() !== '',
              );
              return (
                <li
                  key={match.id}
                  className="flex flex-col gap-2 rounded-md border border-border px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="min-w-0 break-words">
                    {/* The full name AND the short name: identity is being established. */}
                    <span className="block text-xs font-medium text-fg">{match.name}</span>
                    {meta.length > 0 && (
                      <span className="block text-2xs text-fg-muted">{meta.join(' · ')}</span>
                    )}
                    {/* The server's own sentence, verbatim. */}
                    <span className="block text-2xs text-fg-subtle">{match.reason}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={linkingId === match.id}
                    disabled={linking && linkingId !== match.id}
                    onClick={() => void attach({ id: match.id, name: match.name }, false)}
                  >
                    Use this company
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        {/* ⛔ Always offered, never the default — whatever the lookup returned. */}
        <div className="flex flex-col gap-2 rounded-md border border-border px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs font-medium text-fg">None of these</span>
          <Button
            size="sm"
            variant="primary"
            // Busy while a company created here is being attached.
            loading={
              linking &&
              !(lookup.status === 'ready' && lookup.matches.some((m) => m.id === linkingId))
            }
            disabled={linking || lookup.status === 'loading'}
            onClick={() => setStage({ name: 'creating' })}
          >
            Create a new company
          </Button>
        </div>
      </div>
    </Modal>
  );
}
